/**
 * nutri-leaf-product-main.js
 * Nutri-Leaf product page interactions — gallery, AJAX add-to-cart,
 * tabs, variant picker, sticky add-to-cart bar.
 * Custom Element (Dawn convention): lifecycle via connectedCallback /
 * disconnectedCallback. Liquid-rendered config (translations, variant
 * options, subscription interval) is injected via the
 * nl-product-config-{{ section.id }} JSON script tag.
 */
(function () {
  'use strict';

  /**
   * TL-12 (2026-09-06): money-format fallback used when
   * window.Shopify.formatMoney is absent (the old toFixed() replace lost
   * the comma decimals of e.g. €26,11). Placeholder semantics follow the
   * official Shopify currency-formatting table (1,134.56 example; this
   * store's own format "€{{amount_with_comma_separator}}" renders €14,99 —
   * store-verified 2026-09-06):
   *
   *   {{amount}}                                  $1,234.56  (comma group, . dec)
   *   {{amount_no_decimals}}                      $1,235
   *   {{amount_with_comma_separator}}             $1.234,56  (. group, , dec)
   *   {{amount_no_decimals_with_comma_separator}} $1.235
   *   {{amount_with_space_separator}}             $1 234,56  (space group, , dec)
   *   {{amount_with_period_and_space_separator}}  $1 234.56  (space group, . dec)
   *   {{amount_with_apostrophe_separator}}        $1'234.56
   *
   * Fraction digits follow the active currency (JPY -> 0, EUR/USD -> 2).
   * Rounding happens once in exact integer arithmetic.
   */
  function moneyFallback(cents, format, currencyCode) {
    if (typeof cents !== 'number' || !isFinite(cents)) return String(cents);
    if (!format) format = '{{amount}}';
    var frac = 2;
    try {
      var resolved = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (typeof currencyCode === 'string' && currencyCode) || 'EUR'
      }).resolvedOptions().maximumFractionDigits;
      if (typeof resolved === 'number' && isFinite(resolved) && resolved >= 0 && resolved <= 3) {
        frac = resolved;
      }
    } catch (e) { /* unknown currency -> default 2 */ }

    var negative = cents < 0;
    var abs = Math.abs(cents);
    // cents are minor units of a 2-digit scale. Convert to the currency's
    // actual fraction digits with exact integer math.
    var integerPart;
    var fracStr = '';
    if (frac >= 2) {
      var scale = Math.pow(10, frac - 2);
      var scaled = abs * scale; // exact integer
      integerPart = Math.floor(scaled / Math.pow(10, frac));
      fracStr = String(scaled % Math.pow(10, frac));
      while (fracStr.length < frac) fracStr = '0' + fracStr;
    } else {
      // Zero/one-digit currency (JPY …): one half-up round on the whole part.
      integerPart = Math.round(abs / Math.pow(10, 2 - frac));
      if (frac === 1) fracStr = '0';
    }
    // {{amount_no_decimals}} rounds the major-unit value half-up regardless
    // of the currency's fraction digits.
    var integerRounded = Math.round(abs / 100);

    function group(intStr, sep) {
      if (!sep) return intStr;
      var out = '';
      var rest = intStr;
      while (rest.length > 3) {
        out = sep + rest.slice(-3) + out;
        rest = rest.slice(0, -3);
      }
      return rest + out;
    }

    var intPlain = String(integerPart);
    var groupedComma = group(intPlain, ',');
    var groupedSpace = group(intPlain, ' ');
    var groupedPeriod = group(intPlain, '.');
    var groupedApostrophe = group(intPlain, "'");
    var groupedRoundedComma = group(String(integerRounded), ',');
    var groupedRoundedPeriod = group(String(integerRounded), '.');
    var decimalPoint = frac ? '.' + fracStr : '';
    var decimalComma = frac ? ',' + fracStr : '';

    var out = {
      '{{amount}}': groupedComma + decimalPoint,
      '{{amount_no_decimals}}': groupedRoundedComma,
      '{{amount_with_comma_separator}}': groupedPeriod + decimalComma,
      '{{amount_no_decimals_with_comma_separator}}': groupedRoundedPeriod,
      '{{amount_with_space_separator}}': groupedSpace + decimalComma,
      '{{amount_with_period_and_space_separator}}': groupedSpace + decimalPoint,
      '{{amount_with_apostrophe_separator}}': groupedApostrophe + decimalPoint
    };

    // Longest token first so _with_comma_ variants match before {{amount}}.
    var rendered = format.replace(/\{\{amount(?:_no_decimals)?(?:_with_(?:comma|space|period_and_space|apostrophe)_separator)?\}\}/g, function (tok) {
      return out[tok] !== undefined ? out[tok] : tok;
    });
    return negative ? '-' + rendered : rendered;
  }

  // Guarded so `node --test` unit imports can require this file without a DOM.
  const NLProductMain = (typeof HTMLElement !== 'undefined') ? class NLProductMain extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      const self = this;

      const sid = this.dataset.sectionId;
      const configEl = this.querySelector('#nl-product-config-' + sid);
      if (!configEl) return;
      let cfg;
      try {
        cfg = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('[nl-product] invalid config', e);
        return;
      }
      this.initialized = true;
      this.observers = [];
      // P1-12: lifecycle signal — aborts every listener below on disconnect
      this._lifecycle = new AbortController();
      const signal = this._lifecycle.signal;
      const i18n = cfg.i18n || {};
      const i18nAddToCart = i18n.addToCart || 'Add to cart';
      const i18nSoldOut = i18n.soldOut || 'Sold out';
      const i18nAdding = i18n.adding || 'Adding…';
      const i18nAddedToCart = i18n.addedToCart || 'Added to cart';
      const i18nAddToCartError = i18n.addToCartError || 'Could not add to cart. Please try again.';
      let currentOptions = cfg.currentOptions || [];

      // Money formatting (shared by variant price + subscribe price updates).
      // TL-12: prefers Shopify.formatMoney, falls back to the tested
      // Intl-based moneyFallback honoring the injected currency.
      function fmt(cents) {
        const format = (window.nlMoneyFormat || '{{amount}}');
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents, format);
        }
        return moneyFallback(cents, format, cfg.currencyCode);
      }

      // P1-07: shared responsive srcset builder (thumb clicks and variant media swaps)
      const mediaWidths = [360, 533, 720, 940, 1066, 1200, 1800, 2400];
      function mediaSrcset(src) {
        return mediaWidths.map(function (w) {
          return src + '?width=' + w + ' ' + w + 'w';
        }).join(', ');
      }

      // ── Gallery — media-type-aware swaps (TL-06 2026-09-06) ──
      // Helpers live at connectedCallback scope (not inside `if (gallery)`)
      // because the variant-change media swap in updateUI reuses them and
      // must survive video state.
      const gallery = document.getElementById('nl-gallery-' + sid);
      const galleryMain = gallery ? gallery.querySelector('.nl-gallery__main') : null;
      const galleryImgId = 'nl-gallery-main-img-' + sid;

      // Lazy get-or-create: a video swap removes the main <img>, so any
      // reference captured earlier goes stale (image clicks updated a
      // detached node). Recreated on demand.
      const galleryMainImg = function () {
        if (!galleryMain) return null;
        const existing = document.getElementById(galleryImgId);
        if (existing) return existing;
        const img = document.createElement('img');
        img.id = galleryImgId;
        img.loading = 'eager';
        img.alt = '';
        img.style.opacity = '0';
        galleryMain.appendChild(img);
        return img;
      };

      // Pause + tear down whatever video/iframe is playing before a swap.
      // removeAttribute('src') releases the media fetch; the element itself
      // is removed so a later lazy img creation has a clean container.
      const stopGalleryMedia = function () {
        if (!galleryMain) return;
        const old = galleryMain.querySelector('video, iframe');
        if (old) {
          if (old.pause) old.pause();
          old.removeAttribute('src');
          if (old.tagName === 'VIDEO') {
            old.querySelectorAll('source').forEach(function (s) { s.removeAttribute('src'); });
          }
          old.remove();
        }
      };

      // Set an image as the main media (create + crossfade). rawSrc keeps
      // any existing ?width= query; sizing is stripped for the srcset build.
      const galleryShowImageFrom = function (rawSrc, alt, afterSwap) {
        stopGalleryMedia();
        const src = (rawSrc || '').replace(/\?.*$/, '');
        const img = galleryMainImg();
        if (!img || !src) return;
        self._crossfadeMedia(img, src + '?width=1200', mediaSrcset(src), alt || '', afterSwap);
      };

      const galleryShowVideo = function (btn) {
        stopGalleryMedia();
        if (!galleryMain) return;
        const staleImg = document.getElementById(galleryImgId);
        if (staleImg) staleImg.remove();
        const video = document.createElement('video');
        video.className = 'nl-gallery__media-video';
        video.controls = true;
        video.playsInline = true;
        video.preload = 'metadata';
        if (btn.dataset.poster) video.poster = btn.dataset.poster;
        const source = document.createElement('source');
        source.src = btn.dataset.videoSrc || '';
        source.type = btn.dataset.videoMime || 'video/mp4';
        video.appendChild(source);
        galleryMain.appendChild(video);
      };

      const galleryShowExternal = function (btn) {
        const host = btn.dataset.mediaHost || '';
        const id = btn.dataset.externalId || '';
        const baseUrl = host === 'vimeo'
          ? 'https://player.vimeo.com/video/'
          : host === 'youtube'
            ? 'https://www.youtube.com/embed/'
            : '';
        if (!baseUrl || !id) {
          // Unknown host / missing id: show the preview frame image — never
          // a wrong embed URL.
          galleryShowImageFrom(btn.dataset.poster || btn.dataset.src, btn.dataset.alt);
          return;
        }
        stopGalleryMedia();
        if (!galleryMain) return;
        const staleImg = document.getElementById(galleryImgId);
        if (staleImg) staleImg.remove();
        const iframe = document.createElement('iframe');
        iframe.className = 'nl-gallery__media-video';
        iframe.src = baseUrl + id;
        iframe.title = btn.dataset.alt || '';
        iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
        iframe.setAttribute('allowfullscreen', '');
        galleryMain.appendChild(iframe);
      };

      if (gallery) {
        const thumbBtns = gallery.querySelectorAll('.nl-gallery__thumb');
        thumbBtns.forEach(function (btn) {
          btn.addEventListener('click', function (event) {
            thumbBtns.forEach(function (b) {
              b.classList.remove('nl-gallery__thumb--active');
              b.setAttribute('aria-pressed', 'false');
            });
            btn.classList.add('nl-gallery__thumb--active');
            btn.setAttribute('aria-pressed', 'true');

            const type = btn.dataset.mediaType || 'image';
            if (type === 'video') {
              galleryShowVideo(btn);
            } else if (type === 'external_video') {
              galleryShowExternal(btn);
            } else if (type === 'image') {
              galleryShowImageFrom(btn.dataset.src, btn.dataset.alt);
            } else {
              // model / unknown media: honest preview-frame fallback
              galleryShowImageFrom(btn.dataset.poster || btn.dataset.src, btn.dataset.alt);
            }
            // Keyboard activation (click event.detail === 0): move focus to
            // the media container so the next Tab lands somewhere sensible.
            if (galleryMain && event.detail === 0) {
              galleryMain.focus({ preventScroll: true });
            }
          }, { signal });
        }, this);
      }

      // RV-32: honest savings — the subscribe price block renders only when
      // the checked plan has a real adjustment; compare shows the regular
      // variant price so savings are never fabricated. Server renders the
      // adjusted cents per plan into data-sub-price. Recomputes on plan
      // radio change AND on variant change (updateUI), reading the live
      // regular price from #nl-price data-cents.
      const planContainer = document.getElementById('nl-selling-plan-' + sid);
      let updateSubscribePrice = function () {};

      // Selling plan picker — toggle frequency options and subscribe price
      if (planContainer) {
        const purchaseRadios = planContainer.querySelectorAll('input[name="nl-purchase-type-' + sid + '"]');
        const freqBlock = document.getElementById('nl-frequencies-' + sid);
        const freqRadios = planContainer.querySelectorAll('input[name="selling_plan"]');
        const subscribePrice = document.getElementById('nl-subscribe-price-' + sid);
        const subscribeValue = document.getElementById('nl-subscribe-price-value-' + sid);
        const subscribeCompare = document.getElementById('nl-subscribe-compare-' + sid);

        updateSubscribePrice = function () {
          const checked = planContainer.querySelector('input[name="selling_plan"]:checked');
          const priceEl = document.getElementById('nl-price-' + sid);
          if (!checked || !subscribePrice || !subscribeValue || !priceEl) return;
          if (checked.dataset.subHasAdj !== 'true') {
            subscribePrice.style.display = 'none';
            return;
          }
          const pct = parseInt(checked.dataset.subPct || '0', 10);
          const fixed = parseInt(checked.dataset.subFixed || '0', 10);
          const regular = parseInt(priceEl.dataset.cents, 10) || 0;
          const adjusted = Math.max(0, Math.round(regular * (100 - pct) / 100) - fixed);
          subscribeValue.textContent = fmt(adjusted);
          if (subscribeCompare) subscribeCompare.textContent = fmt(regular);
          subscribePrice.style.display = '';
        };

        freqRadios.forEach(function (radio) {
          radio.addEventListener('change', updateSubscribePrice, { signal });
        });

        purchaseRadios.forEach(function (radio) {
          radio.addEventListener('change', function () {
            if (this.value === 'subscribe') {
              if (freqBlock) freqBlock.style.display = '';
              // Pre-select first plan if none selected
              if (freqRadios.length && !planContainer.querySelector('input[name="selling_plan"]:checked')) {
                freqRadios[0].checked = true;
              }
              updateSubscribePrice();
            } else {
              if (freqBlock) freqBlock.style.display = 'none';
              if (subscribePrice) subscribePrice.style.display = 'none';
              // Clear selling_plan selection
              freqRadios.forEach(function (f) { f.checked = false; });
            }
          }, { signal });
        });
      }

      // AJAX Add to Cart — single cart contract (P0-10): NLCart.add() posts to
      // the locale-aware route with mode-matched sections, renders the drawer
      // or notification component, and dispatches cart:refresh for the badge.
      const productForm = document.getElementById('nl-product-form-' + sid);
      if (productForm) {
        productForm.addEventListener('submit', function (e) {
          e.preventDefault();
          const atcBtn = document.getElementById('nl-atc-' + sid);
          if (atcBtn && atcBtn.disabled) return;
          const formData = new FormData(productForm);
          const variantId = parseInt(formData.get('id'), 10);
          const quantity = parseInt(formData.get('quantity') || 1, 10);
          // Selling plan radio (name="selling_plan", checked only in the
          // subscribe mode) — NLCart.add posts it as selling_plan so the
          // line is a subscription, not a one-time purchase (P1-53).
          const planRaw = formData.get('selling_plan');
          const sellingPlanId = planRaw ? parseInt(planRaw, 10) : null;
          if (!window.NLCart) return;
          if (atcBtn) {
            atcBtn.disabled = true;
            atcBtn.textContent = i18nAdding;
          }
          window.NLCart.add(variantId, quantity, sellingPlanId)
            .then(function () {
              if (atcBtn) {
                atcBtn.disabled = false;
                atcBtn.textContent = i18nAddToCart;
              }
              // Show success feedback
              const fb = document.getElementById('nl-atc-feedback-' + sid);
              if (fb) {
                fb.textContent = i18nAddedToCart;
                fb.style.display = 'block';
                fb.classList.add('nl-atc-feedback--success');
                fb.classList.remove('nl-atc-feedback--error');
                setTimeout(function () { fb.style.display = 'none'; }, 5000);
              }
            })
            .catch(function (err) {
              console.error('[nl-product] cart add failed', err);
              const fb = document.getElementById('nl-atc-feedback-' + sid);
              if (fb) {
                fb.textContent = (err && err.message) || i18nAddToCartError;
                fb.style.display = 'block';
                fb.classList.add('nl-atc-feedback--error');
                fb.classList.remove('nl-atc-feedback--success');
                setTimeout(function () { fb.style.display = 'none'; }, 8000);
              }
              if (atcBtn) {
                // P1-08: don't re-enable if variant is truly sold out
                const variantIdInput = document.getElementById('nl-variant-id-' + sid);
                if (variantIdInput && variantIdInput.value) {
                  atcBtn.disabled = false;
                  atcBtn.textContent = i18nAddToCart;
                }
              }
            });
        }, { signal });
      }

      // Supplement facts accordion
      const accToggle = document.querySelector('#shopify-section-' + sid + ' .nl-suppl-accordion__toggle');
      if (accToggle) {
        accToggle.addEventListener('click', function () {
          const body = document.getElementById('nl-suppl-body-' + sid);
          const open = body.classList.toggle('nl-suppl-accordion__body--open');
          body.hidden = !open;
          accToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        }, { signal });
      }

      // Qty stepper
      const qtyInput = document.getElementById('nl-qty-' + sid);
      const qtyMinus = document.getElementById('nl-qty-minus-' + sid);
      const qtyPlus = document.getElementById('nl-qty-plus-' + sid);
      if (qtyInput && qtyMinus && qtyPlus) {
        var stepQty = function (delta) {
          var v = parseInt(qtyInput.value, 10);
          if (isNaN(v)) v = parseInt(qtyInput.min, 10) || 1;
          var min = parseInt(qtyInput.min, 10) || 1;
          var max = parseInt(qtyInput.max, 10);
          var step = parseInt(qtyInput.step, 10) || 1;
          v = v + delta * step;
          if (v < min) v = min;
          if (max && v > max) v = max;
          qtyInput.value = v;
          qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
          qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        };
        qtyMinus.addEventListener('click', function () { stepQty(-1); }, { signal });
        qtyPlus.addEventListener('click', function () { stepQty(1); }, { signal });
      }

      // Product tabs
      const tabsContainer = document.getElementById('nl-tabs-' + sid);
      if (tabsContainer) {
        const tabBtns = tabsContainer.querySelectorAll('.nl-product-tabs__btn');
        const tabPanels = tabsContainer.querySelectorAll('.nl-product-tabs__panel');
        function activateTab(btn) {
          const targetPanelId = btn.getAttribute('aria-controls');
          tabBtns.forEach(function (b) {
            b.classList.remove('nl-product-tabs__btn--active');
            b.setAttribute('aria-selected', 'false');
            b.setAttribute('tabindex', '-1');
          });
          tabPanels.forEach(function (p) { p.classList.remove('nl-product-tabs__panel--active'); p.hidden = true; });
          btn.classList.add('nl-product-tabs__btn--active');
          btn.setAttribute('aria-selected', 'true');
          btn.setAttribute('tabindex', '0');
          if (targetPanelId) {
            const targetPanel = document.getElementById(targetPanelId);
            if (targetPanel) { targetPanel.classList.add('nl-product-tabs__panel--active'); targetPanel.hidden = false; }
          }
        }
        tabBtns.forEach(function (btn) {
          btn.addEventListener('click', function () { activateTab(btn); }, { signal });
        });
        const tabsNav = tabsContainer.querySelector('.nl-product-tabs__nav');
        if (tabsNav) {
          tabsNav.addEventListener('keydown', function (event) {
            const currentIdx = Array.prototype.indexOf.call(tabBtns, document.activeElement);
            if (currentIdx < 0) return;
            let nextIdx = currentIdx;
            if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
              nextIdx = (currentIdx + 1) % tabBtns.length;
            } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
              nextIdx = (currentIdx - 1 + tabBtns.length) % tabBtns.length;
            } else if (event.key === 'Home') {
              nextIdx = 0;
            } else if (event.key === 'End') {
              nextIdx = tabBtns.length - 1;
            } else {
              return;
            }
            event.preventDefault();
            tabBtns[nextIdx].focus();
            activateTab(tabBtns[nextIdx]);
          }, { signal });
        }
      }

      // Variant picker — Dawn-style variant state (availability, invalid guard, media, price/CTA)
      const variantsJson = document.getElementById('nl-product-variants-' + sid);
      if (variantsJson) {
        const variants = JSON.parse(variantsJson.textContent);
        const optionBtns = document.querySelectorAll('#shopify-section-' + sid + ' .nl-variant-option');

        function findVariant(opts) {
          return variants.find(function (v) {
            return v.options.every(function (o, i) { return o === opts[i]; });
          });
        }

        // Dawn getVariantAvailability: a value is selectable when an AVAILABLE
        // variant exists matching current selections on every other option row.
        function optionValueAvailable(optIdx, val) {
          return variants.some(function (v) {
            return v.available && v.options[optIdx] === val && v.options.every(function (o, i) {
              return i === optIdx || currentOptions[i] === undefined || o === currentOptions[i];
            });
          });
        }

        // Dawn setOptionAvailability: mark impossible combinations before selection.
        // The selected value stays enabled even when sold out (Dawn: !available && !selected).
        function setOptionAvailability() {
          optionBtns.forEach(function (btn) {
            const avail = optionValueAvailable(parseInt(btn.dataset.optionIndex, 10), btn.dataset.optionValue);
            const isSelected = btn.classList.contains('nl-variant-option--active');
            btn.classList.toggle('nl-variant-option--unavailable', !avail && !isSelected);
            btn.disabled = !avail && !isSelected;
          });
        }

        // Single state source: every price/availability surface reads `variant`
        const updateUI = (variant) => {
          const idInput = document.getElementById('nl-variant-id-' + sid);
          const priceEl = document.getElementById('nl-price-' + sid);
          const compareEl = document.getElementById('nl-compare-price-' + sid);
          const priceMeta = document.getElementById('nl-price-meta-' + sid);
          const atcBtn = document.getElementById('nl-atc-' + sid);

          // Invalid combination: clear stale variant + price, disable CTA
          if (!variant) {
            if (idInput) idInput.value = '';
            if (priceEl) priceEl.textContent = '';
            if (compareEl) { compareEl.textContent = ''; compareEl.style.display = 'none'; }
            if (priceMeta) priceMeta.setAttribute('content', '');
            if (atcBtn) { atcBtn.disabled = true; atcBtn.textContent = i18nSoldOut; }
            return;
          }

          if (idInput) idInput.value = variant.id;
          if (priceEl) {
            priceEl.textContent = fmt(variant.price);
            priceEl.dataset.cents = String(variant.price);
          }
          if (compareEl) {
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
              compareEl.textContent = fmt(variant.compare_at_price);
              compareEl.style.display = '';
            } else {
              compareEl.textContent = '';
              compareEl.style.display = 'none';
            }
          }
          if (atcBtn) {
            atcBtn.disabled = !variant.available;
            atcBtn.textContent = variant.available ? i18nAddToCart : i18nSoldOut;
          }
          if (priceMeta) priceMeta.setAttribute('content', (variant.price / 100).toFixed(2));
          updateSubscribePrice(); // RV-32: recompute adjusted price on variant change

          // Apply variant quantity rules (P1-03)
          if (qtyInput && variant.quantity_rule) {
            qtyInput.min = variant.quantity_rule.min || 1;
            qtyInput.max = variant.quantity_rule.max || '';
            qtyInput.step = variant.quantity_rule.increment || 1;
            if (parseInt(qtyInput.value, 10) < (variant.quantity_rule.min || 1)) {
              qtyInput.value = variant.quantity_rule.min || 1;
            }
          }

          // Media swap (Dawn currentVariant.featured_media.preview_image
          // pattern). TL-06: uses the same lazy get-or-create + teardown
          // helpers as the thumb clicks, so the swap survives a visible
          // video (video is stopped and removed, image recreated). When the
          // variant's media is already the image on screen (pack variants
          // share one packshot per pack size — this store's data), the
          // display is left untouched instead of re-swapping to the same
          // src. A video currently playing is never replaced by its own
          // poster: variant media differs → swap, same → leave running.
          const galleryMainEl = document.querySelector('#shopify-section-' + sid + ' .nl-gallery__main');
          if (galleryMainEl && galleryMainEl.dataset.gallerySource !== 'manual') {
            const mediaImg = (variant.featured_media && variant.featured_media.preview_image) || variant.featured_image;
            const shownImg = document.getElementById(galleryImgId);
            const shownSrc = shownImg ? ((shownImg.getAttribute('src') || '').replace(/\?.*$/, '')) : '';
            if (mediaImg && mediaImg.src) {
              const src = mediaImg.src.replace(/\?.*$/, '');
              if (src === shownSrc) {
                return; // same media already displayed — keep video/img as-is
              }
              galleryShowImageFrom(mediaImg.src, mediaImg.alt || '', function () {
                document.querySelectorAll('#nl-gallery-' + sid + ' .nl-gallery__thumb').forEach(function (t) {
                  const match = (t.dataset.src || '').replace(/\?.*$/, '') === src;
                  t.classList.toggle('nl-gallery__thumb--active', match);
                  t.setAttribute('aria-pressed', match ? 'true' : 'false');
                });
              });
            }
          }
        }

        optionBtns.forEach(function (btn) {
          btn.addEventListener('click', function () {
            const optIdx = parseInt(btn.dataset.optionIndex, 10);
            const val = btn.dataset.optionValue;
            currentOptions[optIdx] = val;

            // Update active state for this option row
            document.querySelectorAll('#shopify-section-' + sid + ' .nl-variant-option[data-option-index="' + optIdx + '"]').forEach(function (b) {
              b.classList.toggle('nl-variant-option--active', b.dataset.optionValue === val);
              b.setAttribute('aria-pressed', b.dataset.optionValue === val ? 'true' : 'false');
            });

            // Update displayed value label
            const valLabel = document.getElementById('nl-opt-val-' + sid + '-' + optIdx);
            if (valLabel) valLabel.textContent = val;

            setOptionAvailability();
            const matched = findVariant(currentOptions);
            updateUI(matched);

            // Dawn updateURL: keep selected variant in the address bar
            if (matched) {
              const url = new URL(window.location.href);
              url.searchParams.set('variant', matched.id);
              history.replaceState({}, '', url.toString());
            }
          }, { signal });
        });

        // Initial state: mark impossible combinations before any selection
        setOptionAvailability();
      }

      // Sticky add-to-cart bar (mobile)
      const stickyAtc = document.getElementById('nl-sticky-atc-' + sid);
      const stickyAtcPrice = document.getElementById('nl-sticky-atc-price-' + sid);
      const stickyAtcBtn = document.getElementById('nl-sticky-atc-btn-' + sid);
      const atcForm = document.getElementById('nl-product-form-' + sid);
      const atcButton = document.getElementById('nl-atc-' + sid);

      if (stickyAtc && atcForm && atcButton) {
        const stickyObserver = new IntersectionObserver(function (entries) {
          stickyAtc.setAttribute('aria-hidden', entries[0].isIntersecting ? 'true' : 'false');
        }, { threshold: 0 });
        this.observers.push(stickyObserver);
        stickyObserver.observe(atcButton);

        // Sync sticky button click to main form
        stickyAtcBtn.addEventListener('click', function () {
          const mainSubmit = atcForm.querySelector('button[type="submit"]');
          if (mainSubmit) mainSubmit.click();
        }, { signal });

        // Sync sticky button state
        const stickySyncObserver = new MutationObserver(function () {
          stickyAtcBtn.disabled = atcButton.disabled;
          stickyAtcBtn.textContent = atcButton.textContent;
        });
        this.observers.push(stickySyncObserver);
        stickySyncObserver.observe(atcButton, { attributes: true, characterData: true, subtree: true });

        // Update sticky price from main price display
        const mainPriceEl = document.getElementById('nl-price-' + sid);
        if (mainPriceEl && stickyAtcPrice) {
          const priceObserver = new MutationObserver(function () {
            stickyAtcPrice.textContent = mainPriceEl.textContent;
          });
          this.observers.push(priceObserver);
          priceObserver.observe(mainPriceEl, { characterData: true, subtree: true });
          stickyAtcPrice.textContent = mainPriceEl.textContent;
        }
      }
    }

    /**
     * Crossfade main gallery image using CSS transition
     * (transition: opacity .2s on .nl-gallery__main img).
     * Cancels any in-progress crossfade before starting a new one.
     */
    _crossfadeMedia(img, src, srcset, alt, afterSwap) {
      // Cancel pending swap + safety timeout
      if (this._crossfadeHandler) {
        img.removeEventListener('transitionend', this._crossfadeHandler);
        this._crossfadeHandler = null;
      }
      clearTimeout(this._crossfadeSafety);

      const swap = () => {
        clearTimeout(this._crossfadeSafety);
        this._crossfadeHandler = null;
        img.src = src;
        if (srcset) img.srcset = srcset;
        img.alt = alt || '';
        requestAnimationFrame(() => { img.style.opacity = '1'; });
        if (afterSwap) afterSwap();
      };

      // Already invisible (rapid double-swap or reduced-motion) — swap immediately
      if (parseFloat(getComputedStyle(img).opacity) <= 0.05) {
        swap();
        return;
      }

      this._crossfadeHandler = swap;
      img.addEventListener('transitionend', swap, { once: true });
      img.style.opacity = '0';
      // ponytail: safety fallback if transitionend doesn't fire (reduced-motion)
      this._crossfadeSafety = setTimeout(swap, 250);
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.initialized = false;
      // P1-12: abort all signal-bound listeners (gallery, form, tabs, picker, sticky bar)
      if (this._lifecycle) { this._lifecycle.abort(); this._lifecycle = null; }
      // P1-12: cancel pending crossfade to prevent transitionend firing on removed DOM
      clearTimeout(this._crossfadeSafety);
      if (this._crossfadeHandler) {
        const mainImg = document.getElementById('nl-gallery-main-img-' + this.dataset.sectionId);
        if (mainImg) mainImg.removeEventListener('transitionend', this._crossfadeHandler);
        this._crossfadeHandler = null;
      }
      // Disconnect observers so callbacks never fire against removed DOM.
      this.observers.forEach(function (o) { o.disconnect(); });
      this.observers = [];
    }
  } : null;

  if (typeof customElements !== 'undefined' && NLProductMain && !customElements.get('nl-product-main')) {
    customElements.define('nl-product-main', NLProductMain);
  }

  // TL-12: expose the pure formatter for browser-spec cross-checks and
  // unit-test hooks (`node --test` imports this file; customElements is
  // undefined there so only moneyFallback is exported).
  if (typeof window !== 'undefined') {
    window.NLProductMainMoneyFallback = moneyFallback;
  }
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { moneyFallback: moneyFallback };
  }
})();
